// Preparación de las fotos de la preinscripción pública antes de subirlas.
//
// Vivía dentro de PreRegisterPage.jsx (1100 líneas). Se separa porque ahora cada foto
// se sube por su cuenta en cuanto se hace (RF-020) y la página solo debería decidir
// QUÉ subir, no CÓMO se reduce una imagen. Son funciones puras sobre `File`/`Blob`.
import * as Sentry from '@sentry/react';

// Las fotos se enviaban TAL CUAL: la selfie en PNG (toBlob sin tipo) y los DNI como
// los entrega el móvil, 5-10 MB cada uno. Con las tres, el envío rondaba los 20 MB y
// en el wifi del club no llegaba: el socio veía "Enviando…" hasta el timeout y se iba
// (Kong House, 11-08: 7 formularios abiertos, 1 enviado). Redimensionar a 1600 px y
// JPEG 0.82 deja cada foto en ~200-400 KB sin perder legibilidad del documento.
export const MAX_IMAGE_PX = 1600;
export const IMAGE_QUALITY = 0.82;
const SMALL_FILE_BYTES = 800 * 1024;

// …pero ESA copia reducida era también la que leía el OCR, y la MRZ (el reverso) es
// letra de ~2 mm: a 1600 px, con el DNI fotografiado desde lejos, se pierde y el socio ve
// «sin reflejos / foto clara» con una foto perfecta (Kong House, 02-10: Sentry con
// imágenes de 295-459 KB, texto leído y cero líneas de MRZ). El OCR recibe una copia
// mayor; lo que se guarda y se sube sigue siendo la de 1600 px.
const OCR_IMAGE_PX = 2800;
const OCR_IMAGE_QUALITY = 0.9;
const OCR_SMALL_FILE_BYTES = 1500 * 1024;

// `createImageBitmap` / `toBlob` no tienen tiempo máximo y en un móvil viejo pueden no
// volver nunca; con el Enviar bloqueado mientras se escanea, eso dejaría al socio sin
// poder terminar. A los 20 s se sigue con el original (RF-025).
export const PREPARAR_FOTO_MAX_MS = 20000;

// RNF-001: cuándo el móvil no pudo comprimir y se sube el original. Solo el TAMAÑO: ni
// nombre de fichero (puede llevar el del socio), ni token del club, ni contenido. Es la
// señal para saber con datos si los móviles viejos están subiendo 10 MB por foto.
// Nunca lanza: la observabilidad no puede romper el formulario.
export function avisarCompresionFallida(file) {
  try {
    Sentry.captureMessage('prereg-upload: compresion fallida, se sube el original', {
      level: 'warning',
      tags: { area: 'prereg_upload', motivo: 'compresion_fallida' },
      extra: { size_bytes: Number(file?.size) || 0 },
    });
  } catch {
    /* sin Sentry (tests, bloqueador de anuncios): no pasa nada */
  }
}

// Decodifica la foto respetando la orientación EXIF (en navegadores cuyo valor por
// defecto es `none` el DNI saldría de lado y el OCR no encontraría la MRZ: el mismo
// cuidado que `normalizeForOcr` en dniScanner.js, regresión #294) y la libera al acabar.
// Ante cualquier fallo devuelve `fallback` (y avisa con `alFallar`): nunca se bloquea
// por comprimir.
export async function withBitmap(file, fn, fallback, alFallar) {
  try {
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      bitmap = await createImageBitmap(file);     // navegadores viejos ignoran el 2º argumento
    }
    try {
      return await fn(bitmap);
    } finally {
      bitmap.close?.();
    }
  } catch {
    alFallar?.();
    return fallback;
  }
}

// Recodifica un bitmap ya decodificado. Devuelve el propio `file` si ya es pequeño y
// ligero (no se recomprime) y `null` si el navegador no puede codificar: cada llamador
// decide a qué recurre.
export async function resizeBitmap(bitmap, file, { maxPx, quality, smallBytes }) {
  const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size < smallBytes) return file;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality)
  );
  // iOS Safari tiene un tope de memoria de canvas: con dos copias por foto, y anverso y
  // reverso, soltar el lienzo en cuanto se codifica evita que `toBlob` devuelva null.
  canvas.width = 0;
  canvas.height = 0;
  if (!blob) return null;
  const name = (file.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg' });
}

const UPLOAD_SIZE = { maxPx: MAX_IMAGE_PX, quality: IMAGE_QUALITY, smallBytes: SMALL_FILE_BYTES };
const OCR_SIZE = { maxPx: OCR_IMAGE_PX, quality: OCR_IMAGE_QUALITY, smallBytes: OCR_SMALL_FILE_BYTES };

export async function compressImage(file) {
  if (!file || !file.type?.startsWith('image/')) return file;
  const fallar = () => avisarCompresionFallida(file);
  // Si el navegador no puede recodificar, se sube tal cual (y se avisa).
  return withBitmap(file, async (bitmap) => {
    const reducida = await resizeBitmap(bitmap, file, UPLOAD_SIZE);
    if (!reducida) fallar();
    return reducida ?? file;
  }, file, fallar);
}

// Las dos copias de la foto de un documento, decodificando UNA vez (un JPEG de 12 MP
// dos veces en un móvil viejo es memoria que no sobra): `upload` es la que se guarda y
// se sube; `scan` es la que lee el OCR. Si la grande no se puede generar, el OCR usa la
// de subida (el comportamiento de antes): nunca se bloquea por esto.
export async function prepareDniPhotos(original) {
  if (!original || !original.type?.startsWith('image/')) {
    return { upload: original, scan: original };
  }
  const fallar = () => avisarCompresionFallida(original);
  return withBitmap(original, async (bitmap) => {
    const reducida = await resizeBitmap(bitmap, original, UPLOAD_SIZE);
    if (!reducida) fallar();
    const upload = reducida ?? original;
    let scan = upload;
    try {
      scan = (await resizeBitmap(bitmap, original, OCR_SIZE)) ?? upload;
    } catch {
      /* se queda con la de subida */
    }
    return { upload, scan };
  }, { upload: original, scan: original }, fallar);
}

// `compressImage` con el mismo tope que la ruta del DNI: la selfie de galería también
// bloquea Enviar mientras se prepara, y `createImageBitmap`/`toBlob` pueden no volver.
export async function compressImageConTope(file) {
  let temporizador;
  const tope = new Promise((resolve) => {
    temporizador = setTimeout(() => {
      avisarCompresionFallida(file);
      resolve(file);
    }, PREPARAR_FOTO_MAX_MS);
  });
  try {
    return await Promise.race([compressImage(file).catch(() => file), tope]);
  } finally {
    clearTimeout(temporizador);
  }
}

// `prepareDniPhotos` con el tope de PREPARAR_FOTO_MAX_MS: pasado ese tiempo se sigue con
// el original en las dos copias (y se avisa, porque comprimir no llegó a tiempo).
export async function prepareDniPhotosConTope(original) {
  let temporizador;
  const tope = new Promise((resolve) => {
    temporizador = setTimeout(() => {
      avisarCompresionFallida(original);
      resolve({ upload: original, scan: original });
    }, PREPARAR_FOTO_MAX_MS);
  });
  try {
    return await Promise.race([
      prepareDniPhotos(original).catch(() => {
        avisarCompresionFallida(original);
        return { upload: original, scan: original };
      }),
      tope,
    ]);
  } finally {
    clearTimeout(temporizador);
  }
}

// La firma llega del lienzo como data URL y el backend la recibe como FICHERO PNG
// (un campo de formulario de texto tenía tope de 1 MB en Starlette). Devuelve `null`
// si no es un data URL de imagen válido.
export function dataUrlABlob(dataUrl) {
  try {
    const m = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(dataUrl || '');
    if (!m) return null;
    const mime = m[1] || 'image/png';
    const binario = m[2] ? atob(m[3]) : decodeURIComponent(m[3]);
    const bytes = new Uint8Array(binario.length);
    for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  } catch {
    return null;
  }
}
